/**
 * usePreviewFeedback.js
 *
 * Admin-side hook that reads preview feedback from browser-local storage and
 * returns a filtered, projected view of card-template-related issues.
 *
 * Contract source:
 *   07-preview-storage-and-feedback-contract.md
 *   adminPreviewFeedback.js (kea-static-resources) — public read shape.
 *
 * Reading rules:
 *   - Read ONLY through the shared public reader (readPreviewFeedback).
 *   - Do NOT duplicate reader logic locally.
 *   - Expose only the public AdminPreviewIssue shape fields.
 *   - Mark feedback as stale when updated_at is older than 15 minutes.
 *
 * Source filter:
 *   Returns issues where source === PREVIEW_ISSUE_SOURCES.TEMPLATE.
 *   The runtime dispatches 'product_card_template' but previewStorage.js
 *   normalizes it to 'template' before writing to localStorage. The admin
 *   reader must match the stored normalized form (contract §3.2).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  readPreviewFeedback,
  PREVIEW_ISSUE_SOURCES,
} from 'features/shell/preview/readPreviewFeedback';

// Poll interval when preview feedback may be updating
const POLL_INTERVAL_MS = 5000;

/**
 * usePreviewFeedback
 *
 * Reads preview feedback from localStorage and returns card-template issues.
 * Polls every POLL_INTERVAL_MS to pick up runtime feedback changes while the
 * operator has the storefront open in preview mode.
 *
 * @returns {{
 *   issues: Array,       - combined warnings + errors filtered to card-template source
 *   errorCount: number,
 *   warningCount: number,
 *   hasFallback: boolean, - true when an error issue with template_* code exists
 *   isStale: boolean,
 *   previewEnabled: boolean,
 *   updatedAt: string | null
 * }}
 */
export function usePreviewFeedback() {
  const [feedback, setFeedback] = useState(null);
  const intervalRef = useRef(null);
  const mountedRef = useRef(false);

  const load = useCallback(() => {
    const result = readPreviewFeedback();
    if (mountedRef.current) setFeedback(result);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    intervalRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(intervalRef.current);
    };
  }, [load]);

  if (!feedback) {
    return {
      issues: [],
      errorCount: 0,
      warningCount: 0,
      hasFallback: false,
      isStale: true,
      previewEnabled: false,
      updatedAt: null,
    };
  }

  const allIssues = [...feedback.warnings, ...feedback.errors].filter(
    (issue) => issue.source === PREVIEW_ISSUE_SOURCES.TEMPLATE,
  );

  const errors = allIssues.filter((i) => i.severity === 'error');
  const warnings = allIssues.filter((i) => i.severity === 'warning');

  // Fallback is active when there is a compile, render, or unsafe_markup error
  const fallbackCodes = new Set([
    'template_compile_error',
    'template_render_error',
    'template_unsafe_markup',
  ]);
  const hasFallback = errors.some((i) => fallbackCodes.has(i.code));

  return {
    issues: allIssues,
    errorCount: errors.length,
    warningCount: warnings.length,
    hasFallback,
    isStale: feedback.stale,
    previewEnabled: feedback.preview_enabled,
    updatedAt: feedback.updated_at || null,
  };
}
